import { config } from '@config'
import { HttpService } from '@services/HttpService'
import { storage } from '@services/storageService'
import { ORDER_SERVICE_ENDPOINTS } from '../constants/apiConstants'
import { OrderType } from '@modules/common/models/enums/OrderManagement'
import { orderKPIGlobalTopBarStore } from '../stores/OrderKPIGlobalTopBarStore'
import { PRODUCT_SERVICE_ENDPOINTS } from '@modules/showroom/productCollection/enhancedView/constants/cnf-product-constants'
import {
  CLIENT_ORDER_LEVEL_GROUP_ATTR,
  DOOR_ORDER_LEVEL_GROUP_ATTR,
  KPI_TOPBAR_METRIC_ATTRIBUTES
} from '../constants/uiConstants'

export class OrderService {
  httpService = new HttpService()

  getOptions = () => {
    const accessToken: string = storage.getItem('access_token')
    return {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  }

  getClientOrderSummary = async orderId => {
    const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.GET_CLIENT_ORDER_SUMMARY}/${orderId}`

    return this.httpService
      .get(url, {
        headers: this.getOptions()
      })
      .then(response => {
        const doorOrders = response.data?.doorOrders?.filter(item => item.door.name)
        return { ...response.data, doorOrders }
      })
  }

  getTableSetting = async (orderId: string, orderType: string) => {
    const url = `${config.buyingBoardServiceRestEndpoint}${
      orderType === OrderType.Client
        ? ORDER_SERVICE_ENDPOINTS.GET_ORDER_TABLE_SETTING
        : ORDER_SERVICE_ENDPOINTS.GET_DOOR_TABLE_SETTING
    }/${orderId}`

    return this.httpService
      .get(url, {
        headers: this.getOptions()
      })
      .then(response => {
        return response.data || {}
      })
  }

  updateTableSetting = async (orderId: string, data: any, orderType: string) => {
    const url = `${config.buyingBoardServiceRestEndpoint}${
      orderType === OrderType.Client
        ? ORDER_SERVICE_ENDPOINTS.GET_ORDER_TABLE_SETTING
        : ORDER_SERVICE_ENDPOINTS.GET_DOOR_TABLE_SETTING
    }/${orderId}`

    return this.httpService
      .put(url, data, {
        headers: this.getOptions()
      })
      .then(response => {
        return response.data || {}
      })
  }

  getDoorOrderSummary = async orderId => {
    const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.GET_DOOR_ORDER_SUMMARY}/${orderId}`

    return this.httpService
      .get(url, {
        headers: this.getOptions()
      })
      .then(response => response.data)
  }

  getClientOrderNames = async (bsgId, clientId) => {
    try {
      const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.GET_ORDER_NAVIGATION_DATA}/buyingSessionGroup/${bsgId}/client/${clientId}`

      const response = await this.httpService.get(url, {
        headers: this.getOptions()
      })
      return response?.data?.data?.clientOrders
    } catch (e) {
      console.log(e)
      // throw e
    }
  }

  updateSummaryData = async (orderId: string, summary, orderType: string) => {
    const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.UPDATE_SUMMARY_ORDER_DELIVERY_DATE}/${orderId}?orderType=${orderType}`
    const data = {
      orderSummary: summary
    }
    return this.httpService
      .patch(url, data, {
        headers: this.getOptions()
      })
      .then(response => response.data)
  }

  addRemoveDoors = async (
    clientId: string,
    bsgId: string,
    orderId: string,
    doorsToBeAdded: any,
    doorOrdersToBeRemoved: any,
    doorOrdersToBeRestored: any
  ) => {
    try {
      const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.ADD_REMOVE_DOORS}/buyingSessionGroup/${bsgId}/client/${clientId}/clientOrderId/${orderId}`
      const response = await this.httpService.post(
        url,
        {
          ...(doorsToBeAdded ? { doorsToBeAdded } : {}),
          ...(doorOrdersToBeRemoved ? { doorOrdersToBeRemoved } : {}),
          ...(doorOrdersToBeRestored ? { doorOrdersToBeRestored } : {})
        },
        {
          headers: this.getOptions()
        }
      )
      return response.data?.data
    } catch (e) {
      console.log(e)
      throw e
    }
  }

  getEnhancedOTBData = (buyingSessionGroupId: string, clientId: string, body: any) => {
    const url = `${config.buyingBoardServiceRestEndpoint}core/planningBudget/buyingSessionGroup/${buyingSessionGroupId}/client/${clientId}/aggregates`
    return this.httpService
      .post(url, body, { headers: this.getOptions() })
      .then(response => response.data)
  }

  getOTBData = async (orderId, orderType) => {
    const url = `${config.buyingBoardServiceRestEndpoint}order/otb/orderId/${orderId}/orderType/${orderType}`

    return this.httpService
      .get(url, {
        headers: this.getOptions()
      })
      .then(response => response.data)
  }

  updatePORef = async (orderId: string, poRef: string, orderType: string) => {
    const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.UPDATE_SUMMARY_ORDER_DELIVERY_DATE}/${orderId}?orderType=${orderType}`
    const data = {
      orderSummary: {
        poRef
      }
    }
    return this.httpService
      .patch(url, data, {
        headers: this.getOptions()
      })
      .then(response => response.data)
  }

  updateNotes = async (orderId: string, notes: string, orderType: string) => {
    try {
      const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.UPDATE_SUMMARY_ORDER_DELIVERY_DATE}/${orderId}?orderType=${orderType}`
      const data = {
        orderSummary: {
          notes
        }
      }
      const response = await this.httpService.patch(url, data, {
        headers: this.getOptions()
      })
      return response.data
    } catch (e) {
      console.log(e)
      // throw e
    }
  }

  updateDeliveryDate = async (
    orderId: string,
    deliveryDate: number,
    orderType: string
  ) => {
    try {
      const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.UPDATE_SUMMARY_ORDER_DELIVERY_DATE}/${orderId}?orderType=${orderType}`
      const data = {
        orderSummary: {
          deliveryDate
        }
      }
      const response = await this.httpService.patch(url, data, {
        headers: this.getOptions()
      })
      return response.data
    } catch (e) {
      console.log(e)
      // throw e
    }
  }

  applyToAllSummary = async (clientOrderId: string, attributeToCopy: any) => {
    try {
      const url = `${config.buyingBoardServiceRestEndpoint}${ORDER_SERVICE_ENDPOINTS.APPLY_TO_ALL_SUMMARY}/${clientOrderId}`
      const response = await this.httpService.patch(url, attributeToCopy, {
        headers: this.getOptions()
      })
      return response.data
    } catch (e) {
      console.log(e)
    }
  }

  removeProductToOrder = async (
    orderId: string,
    buyingSessionProductId: string,
    orderType: OrderType
  ) => {
    try {
      const urlParam = orderType === OrderType.Client ? 'clientOrder' : 'doorOrder'
      const url = `${config.buyingBoardServiceRestEndpoint}${
        ORDER_SERVICE_ENDPOINTS.GET_ORDER_PRODUCT_DETAILS
      }/${urlParam}/${orderId}/buyingSessionProduct/${buyingSessionProductId}/${
        orderType === OrderType.Client ? 'remove' : 'unbook'
      }`
      const response = await this.httpService.post(
        url,
        {},
        {
          headers: this.getOptions()
        }
      )
      const data = response.data?.data
      const { clientOrder, doorOrder } = data
      orderKPIGlobalTopBarStore.updateOrderKPIByOrderTypes({ clientOrder, doorOrder })
      return data
    } catch (e) {
      console.log(e)
      throw e
    }
  }

  fetchEnhancedOrderTopbarData = (
    bsgId: string,
    orderId: string,
    clientOrderId: string = '',
    orderType: string
  ) => {
    const isDoorOrder = orderType === OrderType.Door
    const body = {
      metricAttributes: KPI_TOPBAR_METRIC_ATTRIBUTES,
      groupBy: [isDoorOrder ? DOOR_ORDER_LEVEL_GROUP_ATTR : CLIENT_ORDER_LEVEL_GROUP_ATTR]
    }
    const urlSuffix = isDoorOrder
      ? `/clientOrder/${clientOrderId}/doorOrder/${orderId}/aggregates`
      : `/clientOrder/${orderId}/aggregates`
    const url = `${config.buyingBoardServiceRestEndpoint}${PRODUCT_SERVICE_ENDPOINTS.ENHANCED_PRODUCT_COMMON_PATH}/${bsgId}${urlSuffix}`
    return this.httpService
      .post(url, body, {
        headers: this.getOptions()
      })
      .then(response => response.data?.data)
  }
}
